import React, { useState, useEffect, useRef } from 'react'
import Api from '../../../../_helpers/Api'
import { trans } from '../../../../_providers/Translation'
// import '../../../../assets/css/ReservationStats.css'
import LatestReservations from './LatestReservations'
import StatusDiagram from './StatusDiagram'
import ReservationMetrics from './ReservationMetrics'
import NoDataFound from '@/components/partials/NoDataFound'
import { Button, Menu, MenuItem, Tooltip } from '@mui/material'
import Skeleton from 'react-loading-skeleton'
import { useHistory } from 'react-router-dom'
import CompactFilterLayout from '@/components/partials/CompactFilterLayout'
import DateRange from '@/components/partials/DateRange'
import filterImage from '@/assets/img/icons/filter.svg'
import { getColorVariationFromPrimaryColorBasedOnIndex, getDarkerColorVariationFromPrimaryColorBasedOnIndex } from '@/_helpers/calculateColor'

const INITIAL_FILTERS = {
    date_from: null,
    date_to: null
}

const ReservationStats = () => {
    const [stats, setStats] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [selectedStatus, setSelectedStatus] = useState(null)
    const [filters, setFilters] = useState(INITIAL_FILTERS)

    const history = useHistory()

    const compactFilterLayoutRef = useRef(null)
    const addAddressReservationButtonRef = useRef(null)
    const fetchDebounceRef = useRef(null)

    const [openButtonDropdown, setOpenButtonDropdown] = useState(null)

    const handleNewTask = () => history.push('/schedule/reservations/address/add')
    const handleNewServiceReservation = () => history.push('/schedule/reservations/service/add')

    function handleButtonSubmenuClick(e) {
        e.stopPropagation()
        e.preventDefault()

        setOpenButtonDropdown(addAddressReservationButtonRef.current)
    }

    useEffect(() => {
        clearTimeout(fetchDebounceRef.current)
        fetchDebounceRef.current = setTimeout(() => {
            fetchReservationStats()
        }, 300)
        return () => clearTimeout(fetchDebounceRef.current)
    }, [filters])

    const fetchReservationStats = async () => {
        try {
            setLoading(true)
            const response = await Api.get(`dashboard/reservation-stats?date_from=${filters?.date_from || ''}&date_to=${filters?.date_to || ''}`)
            setStats(response.data)
            setError(null)
        } catch (err) {
            console.error('Error fetching reservation stats:', err)
            setError('Failed to load reservation statistics')
        } finally {
            setLoading(false)
        }
    }

    const calculateStackedBar = () => {
        if (!stats || !stats.stats || stats.total === 0) return []

        let cumulativePercentage = 0

        return stats.stats.map((item) => {
            const percentage = stats.total > 0 ? (item.count / stats.total) * 100 : 0
            const startPosition = cumulativePercentage
            const endPosition = cumulativePercentage + percentage
            const centerPosition = startPosition + (percentage / 2)

            cumulativePercentage += percentage

            return {
                ...item,
                percentage: Math.round(percentage * 10) / 10, // Round to 1 decimal
                startPosition,
                endPosition,
                centerPosition
            }
        })
    }

    const handleClearFilter = () => {
        setSelectedStatus(null)
    }

    function handleFilterChange(name, value) {
        setFilters(prev => ({
            ...prev,
            [name]: value
        }))
    }

    function getAppliedFiltersCount() {
        if (!filters) return 0
        let count = 0
        if (filters.date_from || filters.date_to) count++
        return count
    }

    const stackedData = calculateStackedBar()

    const handleSegmentClick = (item) => {
        if (selectedStatus && selectedStatus.id === item.id) {
            setSelectedStatus(null)
        } else {
            setSelectedStatus(item)
        }
    }

    return (
        <div className="reservation-stats-container">
            <div className="head">
                <h3>{trans('headings.reservations')}</h3>
                <div className="right">
                    <Menu
                        anchorEl={openButtonDropdown}
                        keepMounted
                        open={Boolean(openButtonDropdown)}
                        onClose={() => setOpenButtonDropdown(null)}
                    >
                        <MenuItem onClick={handleNewTask}>
                            {trans('buttons.addAddressReservation')}
                        </MenuItem>
                        <MenuItem onClick={handleNewServiceReservation}>
                            {trans('buttons.addServiceReservation')}
                        </MenuItem>
                    </Menu>
                    <button onClick={handleNewTask} className="button button-add with-dropdown" ref={addAddressReservationButtonRef} style={{ marginRight: '0' }}>
                        {trans('buttons.new')}
                        <i className="menu" onClick={handleButtonSubmenuClick}>

                        </i>
                    </button>
                    <Button onClick={(e) => {
                        compactFilterLayoutRef.current.open(e)
                    }}>
                        <img src={filterImage} alt="Filter" />
                        {getAppliedFiltersCount() ?
                            <i>
                                {getAppliedFiltersCount()}
                            </i>
                            : <></>}
                    </Button>
                    <CompactFilterLayout
                        ref={compactFilterLayoutRef}
                        appliedFiltersCount={getAppliedFiltersCount()}
                        onClearClick={() => setFilters(INITIAL_FILTERS)}
                    >
                        <div className='row'>
                            <div className='col'>
                                <DateRange
                                    startDate={filters?.date_from || ''}
                                    endDate={filters?.date_to || ''}
                                    onStartDateChange={(date) => handleFilterChange('date_from', date)}
                                    onEndDateChange={(date) => handleFilterChange('date_to', date)}
                                />
                            </div>
                        </div>
                    </CompactFilterLayout>
                </div>
            </div>
            <hr />
            {loading ?
                <Skeleton count={5} height={100} />
                :
                !stats || !stats.stats || stats.stats.length === 0 ?
                    <div
                        className="alert warning"
                        style={{
                            marginBottom: 0,
                            width: '100%',
                        }}
                    >
                        {trans("messages.noDataFound")}
                    </div>
                    :
                    <div className="reservation-stats-content">
                        <div className="stacked-bar-wrapper">
                            <div className="stacked-bar-container">
                                {stackedData.map((item, index) => (
                                    <Tooltip
                                        classes={{ tooltip: 'dashboard-stats-tooltip' }}
                                        arrow key={item.id || index}
                                        title={<>
                                            <p>
                                                {item.name}
                                            </p>
                                            <b>
                                                {item.count}
                                            </b>
                                        </>}
                                        followCursor
                                    >
                                        <div
                                            className={`stacked-bar-segment ${selectedStatus && selectedStatus.id === item.id ? 'selected' : ''}`}
                                            style={{
                                                width: `${item.percentage}%`,
                                                backgroundImage: `linear-gradient(to right, ${item.color}, ${getDarkerColorVariationFromPrimaryColorBasedOnIndex(3, item.color)})`,
                                            }}
                                            onClick={() => handleSegmentClick(item)}
                                        />
                                    </Tooltip>
                                ))}
                            </div>
                        </div>

                        <div className="reservation-stats-legend">
                            {stats.legend && (() => {
                                const itemsPerColumn = 4
                                const columns = []

                                for (let i = 0; i < stats.legend.length; i += itemsPerColumn) {
                                    const columnItems = stats.legend.slice(i, i + itemsPerColumn)
                                    columns.push(
                                        <div key={i} className="legend-column">
                                            {columnItems.map((item, index) => (
                                                <div key={item.name + index} className="legend-item">

                                                    <div className="legend-details">
                                                        <div
                                                            className="legend-color"
                                                            style={{ backgroundColor: item.color }}
                                                        ></div>
                                                        <div className="legend-name">{item.name}</div>

                                                    </div>
                                                    <div className="legend-count">{item.count}</div>
                                                </div>
                                            ))}
                                        </div>
                                    )
                                }

                                return columns
                            })()}
                        </div>

                        <hr />

                        <div className='row'>
                            <ReservationMetrics
                                filters={filters}
                            />
                        </div>
                        <div className='row'>
                            <div className="col">
                                <StatusDiagram
                                    title={trans('labels.initialStatus')}
                                    endpoint="dashboard/diagram/status_initial"
                                    filters={filters}
                                />
                            </div>
                            <div className="col">
                                <StatusDiagram
                                    title={trans('labels.finalStatus')}
                                    endpoint="dashboard/diagram/status_final"
                                    filters={filters}
                                />
                            </div>
                        </div>

                        <div className='row'>
                            <div className='col'>
                                <LatestReservations
                                    statusFilter={selectedStatus}
                                    onClearFilter={handleClearFilter}
                                    filters={filters}
                                />
                            </div>
                        </div>

                    </div>
            }
        </div>
    )
}

export default ReservationStats